import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Divider } from "antd";

import ApiAppLink from "../../api/app-link";
import "./styles.css";

const LOGO_SRC = `${
  process.env.REACT_APP_DEV_URL || document.location.origin
}/api/images/logo.svg`;

/**
 * LF360-1953 — where a reminder email's "Open Application" link lands when the
 * app did not take it.
 *
 * The link is a plain https address on this host, and the phone decides: with
 * the domain claimed by the app it never reaches a browser at all. So this page
 * is what a desktop sees, or a phone without the app installed — and there is
 * no user-agent sniffing here, because by the time this renders the operating
 * system has already had its say.
 *
 * It used to say the web version was being built. It is built, and it ships
 * with the app from now on, so this page is a staging post rather than a
 * destination: it hands over to the web app with the same path and the same
 * query. The path is carried across unchanged — `/app/plan`, `/app/lifebot` are
 * the web app's own routes, the same ones the phone registers — so the address
 * that failed to open an app opens the same screen in a browser, ids and all.
 *
 * The address comes from the backend rather than from this build: the portal is
 * one image for every environment, so a value baked in here would be wrong for
 * two of the three.
 *
 * **There is no "the web app is not there yet" state any more, and the text
 * below is not it.** That was true while the web app was being written; the
 * contract now is that it is deployed with the portal, so the only way the
 * handover fails is a request that did not arrive or a deployment with no host
 * configured — both faults rather than stages. The page says so plainly instead
 * of explaining a rollout that is over.
 *
 * Nothing about the task is shown either way. The ids travel in the URL unread:
 * this page is public and has no session to check anybody against.
 */
function OpenApp() {
  const { t } = useTranslation();
  const location = useLocation();
  const [deciding, setDeciding] = useState(true);

  useEffect(() => {
    let alive = true;

    ApiAppLink.getConfig()
      .then((res) => {
        const base = res?.data?.webappBaseUrl;

        if (!alive) return;

        if (!base) {
          setDeciding(false);
          return;
        }

        // `replace`, not `assign`: this page is a staging post and going Back
        // to it would simply bounce forward again.
        window.location.replace(`${base}${location.pathname}${location.search}`);
      })
      .catch(() => {
        if (alive) {
          setDeciding(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [location.pathname, location.search]);

  return (
    <div className="open-app">
      <div className="open-app__container">
        <div className="open-app__header">
          {/* Not the portal's CompanyBrand: that logo is a link into the admin
              dashboard, and nobody arriving here from a reminder email is going
              there. */}
          <img className="open-app__logo" src={LOGO_SRC} alt="LifeFit360" />
        </div>
        <Divider />
        <div className="open-app__body">
          <h1 className="open-app__title">{t("open_app.title")}</h1>
          {deciding ? (
            <p className="open-app__text">{t("open_app.opening")}</p>
          ) : (
            <>
              <p className="open-app__text">{t("open_app.could_not_open")}</p>
              <p className="open-app__text">{t("open_app.use_the_app")}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OpenApp;
